import { Link } from 'react-router-dom'

import { SimpleTable, TableSkeleton, Alert } from '@/components/ui'
import useCompany from '@/hooks/useCompany'
import Caption from '@/insights/components/Caption'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'

export interface AssetListParams extends InsightParametersType {
  companyId?: number
  assetIds?: string[]
}

const AssetList: InsightDefinition<AssetListParams, Asset[]> = {
  active: true,
  slug: 'asset/list',
  category: 'asset',
  name: 'Asset List',
  description: 'Display a list of assets',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetIds', control: 'AssetDropdownMulti', required: true, assetType: AssetTypeFilter.InTransit },
    ],

    isValid: (parameters: AssetListParams) => {
      if (parameters?.companyId && parameters.assetIds?.length) {
        return true
      }
      return 'Company and at least one Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: AssetListParams) => {
    const { companyId, assetIds } = parameters as AssetListParams
    const assets = assetIds!.map((a) => `id=${a}`).join('&')
    const path = `/assets?companyId=${companyId}&${assets}&skip=0&take=5000&deleted=false`
    const response = await api.get<Asset[]>({ path, origin: 'AssetList', expiresIn: '1h' })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  Display: ({ status, data }) => {
    if (status.code === InsightStatusCode.LOADING) {
      return <TableSkeleton columns={3} withHeader rowHeight={35} />
    }
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const body = Array.isArray(data)
      ? data.map((asset) => ({
          id: asset.id,
          name: <Link to={`/assets/${asset.id}`}>{asset.name}</Link>,
          type: asset.type,
        }))
      : []

    const headers = ['id', 'name', 'type']

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as AssetListParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.assetIds?.length) {
      fields[''] = `${params.assetIds.length} Asset${params.assetIds.length === 1 ? '' : 's'}`
    }
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default AssetList
